import { Application } from '../customer/Application';
import { Person } from '../customer/Person';
import { Policy } from '../customer/Policy';
import { PolicyAgent } from '../customer/PolicyAgent';
import { Product } from '../static/Product';
import { AdvisorInfoElastic } from './AdvisorInfoElastic';
import { SecurityUtils } from '../../../coverpath-common/js/static/SecurityUtils';

export interface JuvieSignerElastic {
  name: string;
  ssn: string;
  email: string;
  phone: string;
}

export interface PersonElastic {
  firstName: string;
  lastName: string;
  middleName: string;
  ssn: string;
  ssnLastFour: string;
  email: string;
  emailSearch: string;
  role: string;
  state: string;
  dob: Date;
  channel: string;
}

export function agentToAdvisor(application: Application, policyAgent: PolicyAgent): AdvisorInfoElastic {
  const agentType = policyAgent ? policyAgent.type : null;
  let advisorType: string = policyAgent ? policyAgent.type : null;

  if (agentType === null) {
    advisorType = 'Primary';
  } else if (agentType === 'additional') {
    advisorType = 'Secondary';
  } else if (agentType === 'servicing') {
    advisorType = 'Servicing';
  }

  const hasParty: boolean = policyAgent && policyAgent.agent && policyAgent.agent.party ? true : false;

  return {
    name: hasParty
      ? (policyAgent.agent.party as Person).firstName + ' ' + (policyAgent.agent.party as Person).lastName
      : null,
    advisorId: policyAgent && policyAgent.agent ? policyAgent.agent.producerId : null,
    advisorSSN: '',
    email: hasParty ? policyAgent.agent.party.email : null,
    phone: hasParty && policyAgent.agent.party.phone ? policyAgent.agent.party.phone.number : null,
    agentAssisted: application && application.isAgentAssistant ? 'Yes' : 'No',
    type: advisorType,
  };
}

export function getAdditionalAdvisors(policy: Policy): AdvisorInfoElastic[] {
  const advisorList: AdvisorInfoElastic[] = [];

  if (policy.getChannelCode() === Product.channels.CAS.code) {
    policy.additionalPolicyAgents.forEach((agent) => advisorList.push(agentToAdvisor(policy.application, agent)));
  }

  return advisorList;
}

export async function getJuvieSigner(policy: Policy): Promise<JuvieSignerElastic> {
  return policy.insured && policy.insured.signer && policy.insured.signer.email
    ? {
        name: policy.insured.signer.fullName,
        ssn: (await SecurityUtils.getPiiValueByKey(policy.insured.signer.SSN, 4)) || '',
        email: policy.insured.signer.email,
        phone: policy.insured.signer.phone ? policy.insured.signer.phone.number : null,
      }
    : null;
}

export async function getOtherOwner(policy: Policy): Promise<PersonElastic> {
  if (policy && policy.isJointOwnership() && policy.otherOwner) {
    const ssn = (await SecurityUtils.getPiiValueByKey(policy.otherOwner.SSN)) || '';
    return {
      firstName: policy.otherOwner.firstName,
      lastName: policy.otherOwner.lastName,
      middleName: policy.otherOwner.middleName,
      ssn: ssn.replace(/-/gi, ''),
      ssnLastFour: ssn.slice(-4),
      email: policy.otherOwner.email,
      emailSearch: policy.otherOwner.email ? policy.otherOwner.email.replace(/[^a-zA-Z0-9]/g, '') : null,
      role: 'OtherOwner',
      state: policy.otherOwner.residentialAddress.state,
      dob: policy.otherOwner.dob,
      channel: policy.customer.channel,
    };
  }
}

export async function getInsured(policy: Policy): Promise<PersonElastic> {
  if (policy && policy.insured && policy.insured.person) {
    let ssn = (await SecurityUtils.getPiiValueByKey(policy.insured.person.SSN)) || '';
    let ssnLastFour = '';
    if (ssn) {
      ssn = ssn.replace(/-/gi, '');
      ssnLastFour = ssn.slice(-4);
    }
    let emailSearch = null;
    if (policy.insured.person.email) {
      emailSearch = policy.insured.person.email.replace(/[^a-zA-Z0-9]/g, '');
    }
    return {
      firstName: policy.insured.person.firstName,
      lastName: policy.insured.person.lastName,
      middleName: policy.insured.person.middleName,
      ssn: ssn,
      ssnLastFour: ssnLastFour,
      email: policy.insured.person.email,
      emailSearch: emailSearch,
      role: 'Insured',
      state: policy.insured.person.residentialAddress.state,
      dob: policy.insured.person.dob,
      channel: policy.getChannelCode(),
    };
  }
}

export async function getOwner(policy: Policy, role: string): Promise<PersonElastic> {
  const owner = policy.ownerPerson && policy.ownerPerson.firstName ? policy.ownerPerson : policy.insured.person;
  if (owner) {
    let ssn = (await SecurityUtils.getPiiValueByKey(owner.SSN)) || '';
    let ssnLastFour = '';
    if (ssn) {
      ssn = ssn.replace(/-/gi, '');
      ssnLastFour = ssn.slice(-4);
    } else {
      ssn = 'None     ';
    }
    let emailSearch = null;
    if (owner.email) {
      emailSearch = owner.email.replace(/[^a-zA-Z0-9]/g, '');
    }

    return {
      firstName: owner.firstName,
      lastName: owner.lastName,
      middleName: owner.middleName,
      ssn: ssn,
      ssnLastFour: ssnLastFour,
      email: owner.email,
      emailSearch: emailSearch,
      role: role,
      state: owner.residentialAddress ? owner.residentialAddress.state : null,
      dob: owner.dob,
      channel: policy.getChannelCode(),
    };
  }
}
